import type { ComponentProps } from 'react';
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { InputField } from '#pie/components/input-field/InputField';
import {
  InputMultiDropdown,
  type InputMultiDropdownProps,
} from '#pie/components/input-multi-dropdown/InputMultiDropdown';
import { useExtendedRules } from '../formHelpers';

type FormMultiDropdownProps<T extends FieldValues> = Omit<
  InputMultiDropdownProps,
  | 'id'
  | 'isError'
  | 'required'
  | 'value'
  | 'onChange'
  | keyof ControllerRenderProps<T, Path<T>>
> &
  Pick<UseControllerProps<T>, 'disabled' | 'name' | 'rules' | 'defaultValue'> &
  Pick<ComponentProps<typeof InputField>, 'label' | 'className'> & {
    onChange?: (value: string[]) => void;
  };

export function FormMultiDropdown<T extends FieldValues>({
  name,
  rules: originalRules,
  defaultValue,
  label,
  disabled,
  className,
  onChange,
  ...props
}: FormMultiDropdownProps<T>) {
  const rules = useExtendedRules(originalRules);
  const {
    fieldState: { error },
    field,
  } = useController({
    defaultValue,
    name,
    rules: {
      ...rules,
      validate: (value) => {
        if (rules?.required && (!Array.isArray(value) || value.length === 0)) {
          return typeof rules.required === 'string' ? rules.required : true;
        }

        if (typeof rules?.validate === 'function') {
          return rules.validate(value, {} as never);
        }

        if (rules?.validate && typeof rules.validate === 'object') {
          for (const validator of Object.values(rules.validate)) {
            const result = validator(value, {} as never);
            if (result !== true) {
              return result;
            }
          }
        }

        return true;
      },
    },
  });

  return (
    <InputField
      name={name}
      isRequired={!!rules?.required}
      label={label}
      error={error?.message}
      className={className}
    >
      <InputMultiDropdown
        isError={!!error}
        required={!!rules?.required}
        onChange={(value) => {
          field.onChange(value);
          onChange?.(value);
        }}
        value={Array.isArray(field.value) ? field.value : []}
        disabled={disabled}
        name={field.name}
        {...props}
      />
    </InputField>
  );
}
